import { defineStore, acceptHMRUpdate } from 'pinia'

import { apiClient, apiBuildTxt } from "@/helpers/api.js"
import { handleApiError } from '@/services/errorHandler'
import { modifiedDateStringForBuild } from "@/helpers/datetime.js"
import { useUserStore } from './UserStore'
import { DEFAULT_TOAST_NOTIFICATION, TOAST_TYPES, TOAST_AUTO_DISMISS_MS } from '@/constants'

export const useGlobalStore = defineStore('GlobalStore', {
  state: () => {
    return {
      windowWidth: null,
      windowHeight: null,
      sidebar: false,
      creditsBarHidden: false,
      creditsMenuOpen: false,
      creditsCollapseDisabled: false,
      userAgent: '',
      pusherLoading: false,
      openPlansModal : false,
      openBuyScansModal: false,
      hideAdminBanner: false,
      stripe: null,
      card: null, // XXX stripe card element? need better name given our app is all about "cards". maybe stripeCard?
      sendFeedbackLoader: false,
      sendFeedbackError: false,
      hasNewVersion: false,
      errors: null,
      filters: [],
      saveActiveFilterFor: {
        activeFilters: '',
        viewOption: '',
        sort: '',
        displayColumns: '',
        columnState: ''
      },
      agBodyScroll: {
        popover: {
          old: { top: 0, left: 0 },
          new: { top: 0, left: 0 },
        },
      },
      cellsLoading: false,
      replaceImageLoader: false,
      mobileScanOpened: false,
      celloAlreadyBooted: false,
      openDialog: false,
      showAddressDialog: false,
      showAddressAlert: false,
      showPlanInfoDialog: false,
      showAddressBanner: false,
      addressWarningMessage: 'The address you entered appears to be incorrect or incomplete. Please update your address to continue.',
      toastNotification: {
        show: false,
        message: '',
        type: 'success',
        title: '',
        action: null
      },
      toastTimeoutId: null,
      toastStartTime: null,
      maintenanceMode: false,
      confirmationModal: {
        show: false,
        confirmationMessage: '',
        confirmButtonText: '',
        confirmLabel: '',
        singleOkButton: false,
        confirmAction: () => {},
        mainConfirmButtonClasses: 'primary-button',
        warningIcon: false,
        logo: null
      },

      openBatchErrorModal: false,
      maintenanceMode: false,
      videoWidgetOpen: false,
      // True while any AppDetailPanel (Order Details, future Inventory /
      // Batch detail panels) is on screen. `detailPanelWidth` is the parsed
      // pixel width of the active panel — `useCdpSupport` adds it to the
      // chat-widget's `right` offset so the bubble doesn't sit on top of
      // the panel's footer buttons.
      detailPanelOpen:  false,
      detailPanelWidth: 0,
      // Height (px) of any sticky bottom bar (currently AppPagination in
      // fixed mode). `useCdpSupport` adds this + a 12px gap to the chat
      // widget's `bottom` offset so the bubble sits above the bar instead
      // of being covered by it. 0 when no bar is mounted.
      paginationBarHeight: 0,
      // True while a date-range popover (AppDateRangePicker) is open.
      // `useCdpSupport` hides the floating chat widget so it can't overlap
      // the calendar, which opens near the bottom-right where the bubble sits.
      datePickerOpen: false,
    }
  },

  getters: {
    showAdminBanner() {
      const userStore = useUserStore()
      return !this.hideAdminBanner && userStore.overrideAuth
    },
  },

  actions: {
    /**
     * Show the global bottom-left toast.
     *
     * Auto-dismisses after `TOAST_AUTO_DISMISS_MS` except for `progress`, which
     * is sticky by design — the caller swaps it for a `completed` / `error`
     * toast when the queued work reports back. Calling this again always clears
     * any pending timer first, so a progress → completed handoff can't be cut
     * short by the previous toast's timeout.
     */
    showToast({ title = '', message = '', type = TOAST_TYPES.SUCCESS, action = null } = {}) {
      if (this.toastTimeoutId) {
        clearTimeout(this.toastTimeoutId)
        this.toastTimeoutId = null
      }

      this.toastNotification = { show: true, title, message, type, action }
      this.toastStartTime = Date.now()

      if (type === TOAST_TYPES.PROGRESS) return

      this.toastTimeoutId = setTimeout(() => {
        this.dismissToast()
      }, TOAST_AUTO_DISMISS_MS)
    },

    dismissToast() {
      if (this.toastTimeoutId) {
        clearTimeout(this.toastTimeoutId)
        this.toastTimeoutId = null
      }

      this.toastNotification = { ...DEFAULT_TOAST_NOTIFICATION }
      this.toastStartTime = null
    },

    async postFeedback(body) {
      try {
        this.sendFeedbackError = false
        this.sendFeedbackLoader = true
        const { data, response } = await apiClient(false, true).post('/feedback', body)

        if (!data || !data.ok) {
          this.sendFeedbackError = true
        }

        this.sendFeedbackLoader = false
      } catch (error) {
        handleApiError(error, { component: 'GlobalStore', action: 'postFeedback' })
        this.sendFeedbackLoader = false
        this.sendFeedbackError = true
      }
    },

    async fetchBuildTxt() {
      try {
        const { data } = await apiBuildTxt().get(`/build.txt?ts=${new Date().getTime()}`)
        
        const buildAttribute = document.body.getAttribute('build')

        if (!buildAttribute || !data) return

        const modifiedDateFromServer = modifiedDateStringForBuild(data)
        const modifiedBuildAttribute = modifiedDateStringForBuild(buildAttribute)



        if (new Date(modifiedBuildAttribute) < new Date(modifiedDateFromServer)) {
          this.hasNewVersion = true
        }

      } catch (error) {
        handleApiError(error, { component: 'GlobalStore', action: 'fetchBuildTxt' })
      }
    },

    async createUtmUser(body) {
      try {
        const { data } = await apiClient().post(`/utm-user`, body)
       
        return data
      } catch (error) {
        handleApiError(error, { component: 'GlobalStore', action: 'createUtmUser' })
      }
    },

    async updateUtmUser(id, body) {
      try {
        const { data } = await apiClient().put(`/utm-user/${id}`, body)
        
        return data
      } catch (error) {
        handleApiError(error, { component: 'GlobalStore', action: 'updateUtmUser' })
      }
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useGlobalStore, import.meta.hot))
}